import { DatePipe, provideCloudinaryLoader } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import {
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  provideRouter,
  Router,
  RouteReuseStrategy,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
  IonicRouteStrategy,
  ModalController,
  PopoverController,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';
import { register as registerSwiper } from 'swiper/element/bundle';

import { firebaseProviders } from '../../common/firebase/firebase-providers';
import { ENVIRONMENT } from '../../common/model/common-environment';
import { TruncateTextPipe } from '../../common/pipe/truncate-text.pipe';
import { AppCheckInterceptor } from '../../common/services/app-check-interceptor.service';
import { HttpInterceptorService } from '../../common/services/http-interceptor.service';
import { LoadingStateService } from '../../common/services/loading-state.service';
import { VERSION } from '../../common/version';
import { AppComponent } from './app/app.component';
import { routes } from './app/routes';
import { environment } from './environments/environment';

Sentry.init({
  dsn: 'https://ac7829d113d24f4ca1e9ac1148b927b4@o4504824450908160.ingest.sentry.io/4504824452415488',
  integrations: [Sentry.browserTracingIntegration()],
  tracePropagationTargets: [environment.apiRestEndpoint],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  release: VERSION.version,
  environment: environment.env,
  sendClientReports: false,
  enabled: environment.env === 'production' || environment.env === 'staging',
});

registerSwiper();

const bootstrap = (): void => {
  void bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection(),
      DatePipe,
      TruncateTextPipe,
      ModalController,
      PopoverController,
      ...firebaseProviders(
        environment.firebaseAppName,
        environment.firebaseOptions,
        environment.recaptchaV3SiteKey,
        true,
      ),
      { provide: ENVIRONMENT, useValue: environment },
      { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AppCheckInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpInterceptorService,
        multi: true,
      },
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler({ showDialog: false }),
      },
      { provide: Sentry.TraceService, deps: [Router] },
      provideHttpClient(),
      provideIonicAngular({ mode: 'md' }),
      provideRouter(routes, withEnabledBlockingInitialNavigation()),
      provideServiceWorker('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000',
      }),
      provideCloudinaryLoader('https://res.cloudinary.com/ledzaphter'),
      provideAppInitializer(() => {
        inject(LoadingStateService);
        inject(Sentry.TraceService);
      }),
    ],
  });
};

if (document.readyState === 'complete') {
  bootstrap();
} else {
  document.addEventListener('DOMContentLoaded', bootstrap);
}
